import React, { useCallback, useEffect, useState, useMemo } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, LogOut, ExternalLink, Archive, ChevronUp, ChevronDown, Lock, Trash2, ArchiveX, Ban, ChevronRight, AlertTriangle } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import ConnectJotformButton from './ConnectJotformButton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import {
  checkJotformConnection,
  fetchJotformForms,
  backupJotformForm,
  fetchAllJotformBackups,
  disconnectJotform,
  type JotformForm,
  type JotformBackup,
} from '@/services/jotformService';
import { getBillingStatusLabel, hasActiveBillingAccess, hasBillingHistory } from '@/lib/billingAccess';
import { formatGraceDeadline, getGraceDaysRemaining } from '@/lib/jotformPlanGrace';
import { cn, isFormNeedsBackup } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTimezone } from "@/contexts/TimezoneContext";
import { useProvider } from "@/contexts/ProviderContext";
import { useAutomation } from '@/hooks/useAutomation';
import { useJotformPlanMatch } from '@/hooks/useJotformPlanMatch';
import { BackupStatusPill, NextRunDisplay } from '@/components/AutomationToggle';
import { logger } from '@/lib/logger';

type SortField = 'name' | 'modified' | 'lastBackup';
type SortOrder = 'asc' | 'desc';
const JOTFORM_NAIVE_DATETIME_REGEX = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

const getStorage = (): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storage = window.localStorage;
    return typeof storage?.getItem === 'function' && typeof storage?.setItem === 'function'
      ? storage
      : null;
  } catch {
    return null;
  }
};

const JotformFormsList: React.FC = () => {
  const queryClient = useQueryClient();
  const { timezone } = useTimezone();
  const { refreshConnectionStatus } = useProvider();
  const { getTrackedForm, syncForms, planUsage, organization } = useAutomation();
  const jotformPlanMatch = useJotformPlanMatch();
  const [backingUpFormId, setBackingUpFormId] = useState<string | null>(null);
  const billingHasHistory = hasBillingHistory(organization);
  const billingAccessActive = hasActiveBillingAccess(organization);
  const billingSuspended = billingHasHistory && !billingAccessActive;
  const upgradeRequired = Boolean(jotformPlanMatch.data?.upgradeRequired);
  const mismatchEnforced = upgradeRequired && Boolean(jotformPlanMatch.data?.enforcementActive);
  const graceEndsLabel = formatGraceDeadline(jotformPlanMatch.data?.graceEndsAt);
  const graceDaysRemaining = getGraceDaysRemaining(jotformPlanMatch.data?.graceEndsAt);

  const storage = getStorage();

  const [activeSortField, setActiveSortField] = useState<SortField>(() => {
    if (!storage) return 'name';
    const saved = storage.getItem('jotformListSortField');
    return (saved as SortField) || 'name';
  });
  const [activeSortOrder, setActiveSortOrder] = useState<SortOrder>(() => {
    if (!storage) return 'asc';
    const saved = storage.getItem('jotformListSortOrder');
    return (saved as SortOrder) || 'asc';
  });
  const [inactiveSortField, setInactiveSortField] = useState<SortField>('name');
  const [inactiveSortOrder, setInactiveSortOrder] = useState<SortOrder>('asc');

  useEffect(() => {
    if (!storage) return;
    storage.setItem('jotformListSortField', activeSortField);
    storage.setItem('jotformListSortOrder', activeSortOrder);
  }, [activeSortField, activeSortOrder, storage]);

  const { data: isConnected, isLoading: isLoadingConnectionStatus, error: connectionError, refetch: refetchConnectionStatus } = useQuery<boolean | null, Error>({
    queryKey: ['jotformConnectionStatus'],
    queryFn: checkJotformConnection,
    staleTime: 1 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  const { data: forms, isLoading: isLoadingForms, error: formsError, refetch: refetchForms } = useQuery<JotformForm[], Error>({
    queryKey: ['jotformForms'],
    queryFn: fetchJotformForms,
    enabled: isConnected === true,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const { data: allUserBackups, isLoading: isLoadingAllUserBackups } = useQuery<JotformBackup[], Error>({
    queryKey: ['allJotformBackups'],
    queryFn: fetchAllJotformBackups,
    enabled: isConnected === true,
  });

  // Sync provider forms with tracked_forms table for automation
  useEffect(() => {
    if (organization?.id && forms && forms.length > 0) {
      syncForms({
        organizationId: organization.id,
        provider: 'jotform',
        forms: forms.map(f => ({ id: f.id, name: f.name, modified: f.modified, status: f.status })),
      });
    }
  }, [forms, organization?.id, syncForms]);

  const backupFormMutation = useMutation<
    { success: boolean; backup: { id: string; formId: string; formName: string; createdAt: string; questionsCount: number; webhooksCount: number } },
    Error,
    { formId: string; formName: string }
  >({
    mutationFn: ({ formId }) => backupJotformForm(formId),
    onMutate: ({ formId }) => {
      setBackingUpFormId(formId);
    },
    onSuccess: (data, variables) => {
      toast({
        title: "Backup Successful",
        description: `Form '${variables.formName}' backed up successfully (${data.backup.questionsCount} questions)`,
      });
      queryClient.invalidateQueries({ queryKey: ['jotformBackups', variables.formId] });
      queryClient.invalidateQueries({ queryKey: ['allJotformBackups'] });
    },
    onError: (error, variables) => {
      if (error.message === "REAUTH_REQUIRED") {
        toast({
          variant: "destructive",
          title: "Backup Failed",
          description: "Re-authentication required. Please disconnect and connect Jotform again."
        });
        return;
      }
      toast({
        variant: "destructive",
        title: "Backup Failed",
        description: error.message || `Could not backup form ${variables.formId}.`,
      });
    },
    onSettled: () => {
      setBackingUpFormId(null);
    }
  });

  const handleDisconnect = async () => {
    try {
      toast({ title: "Disconnecting...", description: "Removing Jotform connection." });
      await disconnectJotform();
      toast({ title: "Disconnected", description: "Successfully disconnected from Jotform." });
      queryClient.setQueryData(['jotformConnectionStatus'], false);
      queryClient.invalidateQueries({ queryKey: ['jotformConnectionStatus'] });
      queryClient.invalidateQueries({ queryKey: ['jotformForms'] });
      queryClient.invalidateQueries({ queryKey: ['allJotformBackups'] });
      refreshConnectionStatus('jotform');
    } catch (error: unknown) {
      logger.error("Error disconnecting Jotform:", error);
      const errorMessage = error instanceof Error ? error.message : "An unexpected error occurred";
      toast({ variant: "destructive", title: "Disconnection Failed", description: errorMessage });
    }
  };

  const handleConnected = () => {
    queryClient.setQueryData(['jotformConnectionStatus'], true);
    queryClient.invalidateQueries({ queryKey: ['jotformConnectionStatus'] });
    refreshConnectionStatus('jotform');
  };

  const handleBackupForm = (form: JotformForm) => {
    backupFormMutation.mutate({ formId: form.id, formName: form.name });
  };

  const parseJotformTimestamp = useCallback((dateString: string | null): Date | null => {
    if (!dateString) return null;

    try {
      if (JOTFORM_NAIVE_DATETIME_REGEX.test(dateString)) {
        return fromZonedTime(dateString.replace(' ', 'T'), timezone);
      }

      const parsed = new Date(dateString);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    } catch {
      return null;
    }
  }, [timezone]);

  const formatDateTimeSplit = (dateString: string | null): { date: string; time: string } => {
    if (!dateString) return { date: 'N/A', time: '' };
    try {
      const parsed = parseJotformTimestamp(dateString);
      if (!parsed) return { date: 'N/A', time: '' };

      const date = formatInTimeZone(parsed, timezone, 'MMM d, yyyy');
      const time = formatInTimeZone(parsed, timezone, 'h:mm a');
      return { date, time };
    } catch {
      return { date: 'N/A', time: '' };
    }
  };

  const getLatestBackupTimestamp = useCallback((formId: string, backups: JotformBackup[] | undefined): number => {
    if (!backups || backups.length === 0) return 0;
    const formBackups = backups.filter(b => b.form_id_external === formId);
    if (formBackups.length === 0) return 0;
    try {
      const parsed = parseJotformTimestamp(formBackups[0].created_at);
      return parsed ? parsed.getTime() : 0;
    } catch {
      return 0;
    }
  }, [parseJotformTimestamp]);

  const jotformNeedsBackup = (lastModified: string | null, lastBackup: string | null): boolean => {
    if (!lastBackup) return true;
    if (!lastModified) return false;

    const modifiedDate = parseJotformTimestamp(lastModified);
    const backupDate = parseJotformTimestamp(lastBackup);
    if (!modifiedDate || !backupDate) {
      return isFormNeedsBackup(lastModified, lastBackup);
    }

    return modifiedDate.getTime() > backupDate.getTime();
  };

  const handleActiveSort = (field: SortField) => {
    if (activeSortField === field) {
      setActiveSortOrder(activeSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setActiveSortField(field);
      setActiveSortOrder('asc');
    }
  };

  const handleInactiveSort = (field: SortField) => {
    if (inactiveSortField === field) {
      setInactiveSortOrder(inactiveSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setInactiveSortField(field);
      setInactiveSortOrder('asc');
    }
  };

  const sortForms = useCallback((list: JotformForm[], field: SortField, order: SortOrder) => {
    return [...list].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      switch (field) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'modified':
          aValue = parseJotformTimestamp(a.modified)?.getTime() || 0;
          bValue = parseJotformTimestamp(b.modified)?.getTime() || 0;
          break;
        case 'lastBackup':
          aValue = getLatestBackupTimestamp(a.id, allUserBackups);
          bValue = getLatestBackupTimestamp(b.id, allUserBackups);
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return order === 'asc' ? -1 : 1;
      if (aValue > bValue) return order === 'asc' ? 1 : -1;
      return 0;
    });
  }, [allUserBackups, getLatestBackupTimestamp, parseJotformTimestamp]);

  const VISIBLE_LIMIT = 10;
  const [showAllActive, setShowAllActive] = useState(false);
  const [showAllInactive, setShowAllInactive] = useState(false);
  const [inactiveExpanded, setInactiveExpanded] = useState(false);

  // Split forms into active and inactive groups (sort-independent)
  const { activeForms, inactiveForms } = useMemo(() => {
    if (!forms || !forms.length) return { activeForms: [] as JotformForm[], inactiveForms: [] as JotformForm[] };

    const formLimit = mismatchEnforced ? (planUsage?.formLimit ?? Infinity) : Infinity;

    // Determine which non-inactive forms are covered by the plan limit
    // Use a stable ordering (tracked first) so plan limit doesn't shift with sorts
    const statusActive = forms.filter(f => f.status !== 'DELETED' && f.status !== 'DISABLED' && f.archived !== true);
    const trackedIds = statusActive
      .filter(f => !!getTrackedForm(f.id, 'jotform'))
      .map(f => f.id);
    const untrackedIds = statusActive
      .filter(f => !getTrackedForm(f.id, 'jotform'))
      .map(f => f.id);
    const coveredIds = new Set([
      ...trackedIds.slice(0, formLimit),
      ...untrackedIds.slice(0, Math.max(0, formLimit - trackedIds.length)),
    ]);

    const active: JotformForm[] = [];
    const inactive: JotformForm[] = [];

    for (const form of forms) {
      const isTrashed = form.status === 'DELETED';
      const isDisabled = form.status === 'DISABLED';
      const isArchived = form.archived === true;
      const isOverLimit = formLimit !== Infinity && !isTrashed && !isDisabled && !isArchived && !coveredIds.has(form.id);

      if (isTrashed || isDisabled || isArchived || isOverLimit) {
        inactive.push(form);
      } else {
        active.push(form);
      }
    }

    return { activeForms: active, inactiveForms: inactive };
  }, [forms, getTrackedForm, mismatchEnforced, planUsage]);

  // Sort each section independently
  const sortedActiveForms = useMemo(
    () => sortForms(activeForms, activeSortField, activeSortOrder),
    [activeForms, activeSortField, activeSortOrder, sortForms]
  );
  const sortedInactiveForms = useMemo(
    () => sortForms(inactiveForms, inactiveSortField, inactiveSortOrder),
    [inactiveForms, inactiveSortField, inactiveSortOrder, sortForms]
  );

  const SortableHeader = ({ field, children, section }: { field: SortField; children: React.ReactNode; section: 'active' | 'inactive' }) => {
    const currentField = section === 'active' ? activeSortField : inactiveSortField;
    const currentOrder = section === 'active' ? activeSortOrder : inactiveSortOrder;
    const handler = section === 'active' ? handleActiveSort : handleInactiveSort;
    return (
      <TableHead
        className="cursor-pointer hover:bg-muted/50 select-none"
        onClick={() => handler(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          {currentField === field && (
            currentOrder === 'asc' ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />
          )}
        </div>
      </TableHead>
    );
  };

  if (isLoadingConnectionStatus || (isConnected && (isLoadingForms || isLoadingAllUserBackups))) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="ml-2 text-muted-foreground">
          {isLoadingConnectionStatus ? "Checking Jotform connection..." : "Loading Jotform data..."}
        </p>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="container mx-auto py-8 text-center">
        <Alert variant="destructive">
          <AlertTitle>Connection Check Failed</AlertTitle>
          <AlertDescription>
            Could not verify Jotform connection: {connectionError.message}.
            <Button variant="link" onClick={() => refetchConnectionStatus()} className="ml-2">Try again</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isConnected === null) {
    return (
      <div className="container mx-auto py-8 text-center">
        <Alert variant="default">
          <AlertTitle>Connection Status Unknown</AlertTitle>
          <AlertDescription>
            Could not determine Jotform connection status.
            <Button variant="link" onClick={() => refetchConnectionStatus()} className="ml-2">Refresh Status</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!isConnected) {
    return (
      <div className="container mx-auto py-8 text-center">
        <Alert>
          <AlertTitle className="flex items-center justify-center"><ExternalLink className="h-5 w-5 mr-2"/>Connect to Jotform</AlertTitle>
          <AlertDescription>
            To view and manage your Jotform forms, please connect your account.
          </AlertDescription>
        </Alert>
        <div className="mt-6 max-w-md mx-auto">
          <ConnectJotformButton onConnected={handleConnected} />
        </div>
      </div>
    );
  }

  if (formsError) {
    if (formsError.message === "REAUTH_REQUIRED") {
      return (
        <div className="container mx-auto py-8 text-center">
          <Alert variant="destructive">
            <AlertTitle>Reconnection Required</AlertTitle>
            <AlertDescription>
              Your Jotform connection has expired or is invalid. Please connect again.
            </AlertDescription>
          </Alert>
          <div className="mt-6 flex flex-col items-center gap-4">
            <div className="max-w-md w-full">
              <ConnectJotformButton onConnected={handleConnected} />
            </div>
            <Button variant="outline" onClick={handleDisconnect}>
              <LogOut className="mr-2 h-4 w-4" /> Disconnect (Clear Old Connection)
            </Button>
          </div>
        </div>
      );
    }
    return (
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold">Your Jotform Forms</h2>
            <a
              href="https://www.jotform.com/myforms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline flex items-center gap-1 text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              Open Jotform
            </a>
          </div>
          <Button variant="outline" onClick={handleDisconnect}>
            <LogOut className="mr-2 h-4 w-4" /> Disconnect Jotform
          </Button>
        </div>
        <Alert variant="destructive">
          <AlertTitle>Error Loading Forms</AlertTitle>
          <AlertDescription>
            Could not load forms from Jotform: {formsError.message}
            <Button variant="link" onClick={() => refetchForms()} className="ml-2">Try again</Button>
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="container mx-auto py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-semibold">Your Jotform Forms</h2>
            <a
              href="https://www.jotform.com/myforms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary hover:underline flex items-center gap-1 text-sm"
            >
              <ExternalLink className="h-4 w-4" />
              Open Jotform
            </a>
          </div>
          <Button variant="outline" onClick={handleDisconnect}>
            <LogOut className="mr-2 h-4 w-4" /> Disconnect Jotform
          </Button>
        </div>
        {billingSuspended && (
          <Alert className="mb-6 border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Billing inactive</AlertTitle>
            <AlertDescription>
              {getBillingStatusLabel(organization?.billing_status)}. Automatic backups still run and all prior data is preserved, but form links and manual backup actions are disabled until billing is reinstated.
              <div className="mt-3">
                <Button asChild size="sm">
                  <Link to="/select-tier?action=reinstate">Reinstate Plan</Link>
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}
        {!billingSuspended && jotformPlanMatch.data && upgradeRequired && !mismatchEnforced && (
          <Alert className="mb-6 border-blue-300 bg-blue-50 text-blue-950 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-100">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Upgrade grace period active</AlertTitle>
            <AlertDescription>
              Your Jotform account now maps to the {jotformPlanMatch.data.matchedTier.displayName} FAB plan.
              {graceEndsLabel
                ? ` Upgrade FAB by ${graceEndsLabel}${graceDaysRemaining !== null ? ` (${graceDaysRemaining} day${graceDaysRemaining === 1 ? '' : 's'} remaining)` : ''} to keep every form link and backup-history view available.`
                : ' Upgrade FAB within your 14-day grace period to keep every form link and backup-history view available.'}
              <div className="mt-3">
                <Button asChild size="sm" variant="outline" className="border-blue-300 bg-white/80 text-blue-900 hover:bg-white dark:border-blue-800 dark:bg-blue-950/60 dark:text-blue-100">
                  <Link to="/select-tier?action=upgrade">Review Upgrade</Link>
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}
        {!billingSuspended && jotformPlanMatch.data && upgradeRequired && mismatchEnforced && (
          <Alert className="mb-6 border-amber-300 bg-amber-50 text-amber-950 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-100">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Upgrade required</AlertTitle>
            <AlertDescription>
              Your Jotform account now exceeds your billed FAB plan, and the grace period
              {graceEndsLabel ? ` ended on ${graceEndsLabel}` : ' has ended'}.
              Forms beyond your current FAB visibility limit are now greyed out and their links are hidden, but backups still continue for all forms.
              <div className="mt-3">
                <Button asChild size="sm">
                  <Link to="/select-tier?action=upgrade">Upgrade FAB</Link>
                </Button>
              </div>
            </AlertDescription>
          </Alert>
        )}
        {(!forms || forms.length === 0) ? (
          <Alert>
            <AlertTitle>No Forms Found</AlertTitle>
            <AlertDescription>
              No forms were found in your Jotform account.
              <Button variant="link" onClick={() => refetchForms()} className="ml-2">Try refreshing the list</Button>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            {/* Active Forms Section */}
            {activeForms.length > 0 && (
              <div className="mb-8">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                  Active Forms
                  <Badge variant="secondary" className="text-xs font-normal">{activeForms.length}</Badge>
                </h3>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <SortableHeader field="name" section="active">Name</SortableHeader>
                        <TableHead>Last Auto Backup</TableHead>
                        <TableHead>Next Run</TableHead>
                        <SortableHeader field="modified" section="active">Form Last Modified</SortableHeader>
                        <SortableHeader field="lastBackup" section="active">Last Backup</SortableHeader>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(showAllActive ? sortedActiveForms : sortedActiveForms.slice(0, VISIBLE_LIMIT)).map((form) => {
                        const latestBackup = allUserBackups?.find(b => b.form_id_external === form.id);
                        const needsBackup = jotformNeedsBackup(form.modified, latestBackup?.created_at || null);
                        const tracked = getTrackedForm(form.id, 'jotform');

                        return (
                          <TableRow key={form.id} className={billingSuspended ? 'opacity-60' : undefined}>
                            <TableCell className="font-medium">
                              <div className="flex items-center gap-2">
                                {billingSuspended ? (
                                  <span className="text-muted-foreground">
                                    {form.name.length > 75 ? `${form.name.substring(0, 75)}...` : form.name}
                                  </span>
                                ) : form.name.length > 75 ? (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Link to={`/jotform/${form.id}`} className="hover:underline text-primary">
                                        {form.name.substring(0, 75)}...
                                      </Link>
                                    </TooltipTrigger>
                                    <TooltipContent><p>{form.name}</p></TooltipContent>
                                  </Tooltip>
                                ) : (
                                  <Link to={`/jotform/${form.id}`} className="hover:underline text-primary">
                                    {form.name}
                                  </Link>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <BackupStatusPill
                                state={tracked?.last_backup_state || null}
                                timestamp={tracked?.last_backup_at}
                              />
                            </TableCell>
                            <TableCell>
                              {/* Backup scheduling is org-level, not per-form. All
                                  forms in this org share the same next run. */}
                              <NextRunDisplay
                                nextRunAt={organization?.next_scheduled_backup_at || null}
                                automationEnabled={tracked?.automation_enabled ?? false}
                              />
                            </TableCell>
                            <TableCell className={cn(needsBackup && 'text-red-500')}>
                              {needsBackup ? (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex flex-col">
                                      <span>{formatDateTimeSplit(form.modified).date}</span>
                                      <span className="text-sm text-muted-foreground">{formatDateTimeSplit(form.modified).time}</span>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Please backup soon. Your latest edits are not backed up currently.</p>
                                  </TooltipContent>
                                </Tooltip>
                              ) : (
                                <div className="flex flex-col">
                                  <span>{formatDateTimeSplit(form.modified).date}</span>
                                  <span className="text-sm text-muted-foreground">{formatDateTimeSplit(form.modified).time}</span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              {(() => {
                                const backups = allUserBackups?.filter(b => b.form_id_external === form.id);
                                if (!backups || backups.length === 0) return 'N/A';
                                const { date, time } = formatDateTimeSplit(backups[0].created_at);
                                return (
                                  <div className="flex flex-col">
                                    <span>{date}</span>
                                    <span className="text-sm text-muted-foreground">{time}</span>
                                  </div>
                                );
                              })()}
                            </TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                onClick={() => handleBackupForm(form)}
                                disabled={billingSuspended || backingUpFormId === form.id}
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                {backingUpFormId === form.id ? <Loader2 className="h-4 w-4 animate-spin mr-1"/> : <Archive className="h-4 w-4 mr-1" />}
                                {billingSuspended ? 'Unavailable' : 'Backup'}
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
                {activeForms.length > VISIBLE_LIMIT && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2 text-muted-foreground"
                    onClick={() => setShowAllActive(!showAllActive)}
                  >
                    <ChevronRight className={cn("h-4 w-4 mr-1 transition-transform", showAllActive && "rotate-90")} />
                    {showAllActive ? "Show less" : `Show ${activeForms.length - VISIBLE_LIMIT} more`}
                  </Button>
                )}
              </div>
            )}

            {/* Inactive Forms Section */}
            {inactiveForms.length > 0 && (
              <div>
                <button
                  className="flex items-center gap-2 text-lg font-semibold mb-3 hover:text-foreground/80 transition-colors"
                  onClick={() => setInactiveExpanded(!inactiveExpanded)}
                >
                  <ChevronRight className={cn("h-5 w-5 transition-transform", inactiveExpanded && "rotate-90")} />
                  Inactive Forms
                  <Badge variant="outline" className="text-xs font-normal">{inactiveForms.length}</Badge>
                </button>
                {inactiveExpanded && (
                  <>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <SortableHeader field="name" section="inactive">Name</SortableHeader>
                            <TableHead>Status</TableHead>
                            <SortableHeader field="modified" section="inactive">Form Last Modified</SortableHeader>
                            <SortableHeader field="lastBackup" section="inactive">Last Backup</SortableHeader>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(showAllInactive ? sortedInactiveForms : sortedInactiveForms.slice(0, VISIBLE_LIMIT)).map((form) => {
                            const isTrashed = form.status === 'DELETED';
                            const isDisabled = form.status === 'DISABLED';
                            const isArchived = form.archived === true;
                            const formLimit = planUsage?.formLimit ?? Infinity;
                            const isOverLimit = formLimit !== Infinity && !isTrashed && !isDisabled && !isArchived;

                            return (
                              <TableRow key={form.id} className="opacity-50">
                                <TableCell className="font-medium">
                                  <div className="flex items-center gap-2">
                                    {isOverLimit && <Lock className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                                    {isTrashed && <Trash2 className="h-3.5 w-3.5 text-destructive shrink-0" />}
                                    {isArchived && !isTrashed && <ArchiveX className="h-3.5 w-3.5 text-amber-500 shrink-0" />}
                                    {isDisabled && !isTrashed && !isArchived && <Ban className="h-3.5 w-3.5 text-muted-foreground shrink-0" />}
                                    {form.name.length > 75 ? (
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="text-muted-foreground">
                                            {form.name.substring(0, 75)}...
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent><p>{form.name}</p></TooltipContent>
                                      </Tooltip>
                                    ) : (
                                      <span className="text-muted-foreground">
                                        {form.name}
                                      </span>
                                    )}
                                  </div>
                                  {isOverLimit && (
                                    <p className="text-xs text-muted-foreground mt-1">
                                      This form is still backed up automatically, but app access is hidden on your current plan.
                                    </p>
                                  )}
                                </TableCell>
                                <TableCell>
                                  {isTrashed && <Badge variant="destructive" className="text-xs">Trashed</Badge>}
                                  {isArchived && !isTrashed && <Badge variant="outline" className="text-xs border-amber-500 text-amber-600">Archived</Badge>}
                                  {isDisabled && !isTrashed && !isArchived && <Badge variant="secondary" className="text-xs">Disabled</Badge>}
                                  {isOverLimit && <Badge variant="outline" className="text-xs">Over Plan Limit</Badge>}
                                </TableCell>
                                <TableCell>
                                  <div className="flex flex-col">
                                    <span>{formatDateTimeSplit(form.modified).date}</span>
                                    <span className="text-sm text-muted-foreground">{formatDateTimeSplit(form.modified).time}</span>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {(() => {
                                    const backups = allUserBackups?.filter(b => b.form_id_external === form.id);
                                    if (!backups || backups.length === 0) return 'N/A';
                                    const { date, time } = formatDateTimeSplit(backups[0].created_at);
                                    return (
                                      <div className="flex flex-col">
                                        <span>{date}</span>
                                        <span className="text-sm text-muted-foreground">{time}</span>
                                      </div>
                                    );
                                  })()}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                    {inactiveForms.length > VISIBLE_LIMIT && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-muted-foreground"
                        onClick={() => setShowAllInactive(!showAllInactive)}
                      >
                        <ChevronRight className={cn("h-4 w-4 mr-1 transition-transform", showAllInactive && "rotate-90")} />
                        {showAllInactive ? "Show less" : `Show ${inactiveForms.length - VISIBLE_LIMIT} more`}
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </TooltipProvider>
  );
};

export default JotformFormsList;
